import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { SearchHeader } from '../common'
import {
  TimelineDialog,
  ShipmentPlanDialog,
  RepairTimelineDialog,
  LegPhotosDialog,
  TransportTruckCardDialog,
} from '../dialogs'
import BulkCustomerVehiclesTimelineNoteDialog from '../dialogs/BulkCustomerVehiclesTimelineNoteDialog'
import {
  useLogistics,
  useRepairTrucksForTrucks,
  type TransportTruckWithVehicle,
} from '@/hooks/logistics/useLogistics'
import { useClientUsers, useUsers } from 'hooks/useUsers'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from 'hooks/inventory/useRepairShopLocations'
import { getLegStatus, getTruckStatus, ShipmentPlan } from '@otw/models/logistics'
import { useTransporterGroups } from '@/hooks/admin/useTransporterGroups'
import { VehiclesV2Grid } from '../views'
import {
  CUSTOMER_PICKUP_COMPANY_ID,
  formatTransporterDisplayName,
  SHOP_TRANSPORT_COMPANY_ID,
  type VehiclesV2Row,
} from '../views/VehiclesV2Grid'
import type { RepairTruckWithTruck } from '../views/RepairTrackingView'
import { EditVehicleForm } from '@/components/inventory/forms'

const OPTIC_FILTER_VALUE = 'OPTIC'
const OPTIC_FILTER_LABEL = 'Optic truck works'
const UNASSIGNED_CUSTOMER_LABEL = 'Unassigned'
const PENDING_CUSTOMER_STATUSES = new Set(['Pending Sale', 'Active Deal'])
/** Not real transporter groups — excluded from useTransporterGroups lookup only. */
const NON_COMPANY_IDS = new Set([SHOP_TRANSPORT_COMPANY_ID, CUSTOMER_PICKUP_COMPANY_ID])
const NO_CURRENT_LOCATION_FILTER_VALUE = '__NO_CURRENT_LOCATION__'

type CustomerDisplay = {
  label: string
}

type InventoryStatusDisplay = {
  label: string
  chipColor: 'default' | 'info' | 'error' | 'warning'
}

const VehiclesTabV2: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [customerFilter, setCustomerFilter] = useState<string>('ALL')
  const [transporterFilter, setTransporterFilter] = useState<string>('ALL')
  const [currentLocationFilter, setCurrentLocationFilter] = useState<string>('ALL')
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const [timelineTruckId, setTimelineTruckId] = useState<string | null>(null)
  const [repairTimelineTruckId, setRepairTimelineTruckId] = useState<string | null>(null)
  const [shipmentPlanTruckId, setShipmentPlanTruckId] = useState<string | null>(null)
  const [inventoryFormVehicleId, setInventoryFormVehicleId] = useState<string | null>(null)
  const [shipmentPlanToEdit, setShipmentPlanToEdit] = useState<ShipmentPlan | null>(null)
  const [insertRepairAfterLegIndex, setInsertRepairAfterLegIndex] = useState<number | null>(null)
  const [legPhotosState, setLegPhotosState] = useState<{
    planId: string
    plan: ShipmentPlan
    legItems: {
      fromLabel: string
      toLabel: string
      company_id?: string
      to_type?: 'pickup' | 'repair'
      latestStatusNotes?: string
    }[]
    initialLegIndex?: number
    vehicle_damage?: ShipmentPlan['vehicle_damage'] | null
  } | null>(null)
  const [bulkNoteDialogOpen, setBulkNoteDialogOpen] = useState(false)
  const [transportCardTruckId, setTransportCardTruckId] = useState<string | null>(null)

  const {
    transportTrucksWithVehicles,
    isLoadingTransportTrucks,
    addRepairTruckNote,
    updateRepairTruckNote,
    deleteRepairTruckNote,
    updateRepairTruckWorkStatus,
    reviewRepairTruckEstimate,
    updateRepairTruckPaid,
    updateRepairTruckBeingPainted,
    updateRepairTruckFlagged,
    approveLegPhotos,
    isAddingRepairTruckNote,
    isUpdatingRepairTruckNote,
    isDeletingRepairTruckNote,
    isUpdatingRepairTruckWorkStatus,
    isReviewingRepairTruckEstimate,
    isUpdatingRepairTruckPaid,
    isUpdatingRepairTruckBeingPainted,
    isUpdatingRepairTruckFlagged,
  } = useLogistics()
  const repairTrucksMap = useRepairTrucksForTrucks(transportTrucksWithVehicles.map((t) => t.truck.id))
  const { clientUsers } = useClientUsers()
  const { users } = useUsers()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const clientUserById = useMemo(() => {
    return new Map(clientUsers.map((client) => [client.id, client] as const))
  }, [clientUsers])

  const getCustomerDisplay = (
    shipmentPlans: TransportTruckWithVehicle['shipmentPlans'],
    vehicle: TransportTruckWithVehicle['vehicle']
  ): CustomerDisplay => {
    const latestCustomerPlan = [...(shipmentPlans ?? [])]
      .sort((a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0))
      .find((plan) => plan.client_user_id && plan.client_user_id !== 'optic')

    if (latestCustomerPlan?.client_user_id) {
      const customer = clientUserById.get(latestCustomerPlan.client_user_id)
      if (customer) {
        const label =
          `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() ||
          customer.email ||
          customer.id
        return { label }
      }
      return { label: latestCustomerPlan.client_user_id }
    }

    if (vehicle?.sold_to_user?.name?.trim()) {
      return { label: UNASSIGNED_CUSTOMER_LABEL }
    }

    if (
      vehicle?.selling_to_user?.name?.trim() &&
      PENDING_CUSTOMER_STATUSES.has(vehicle.status ?? '')
    ) {
      return {
        label: UNASSIGNED_CUSTOMER_LABEL,
      }
    }

    if (vehicle?.selling_to_user?.name?.trim()) {
      return { label: UNASSIGNED_CUSTOMER_LABEL }
    }

    return { label: UNASSIGNED_CUSTOMER_LABEL }
  }
  const getInventoryStatusDisplay = (
    vehicle: TransportTruckWithVehicle['vehicle']
  ): InventoryStatusDisplay => {
    const status = vehicle?.status ?? null
    const soldToName = vehicle?.sold_to_user?.name?.trim()
    const sellingToName = vehicle?.selling_to_user?.name?.trim()
    if (!status) return { label: 'Unknown', chipColor: 'default' }
    if (status === 'Sold') {
      return { label: soldToName ? `Sold: ${soldToName}` : status, chipColor: 'error' }
    }
    if (status === 'Pending Sale') {
      return { label: sellingToName ? `Pending Sale: ${sellingToName}` : status, chipColor: 'warning' }
    }
    if (status === 'Active Deal') {
      return { label: sellingToName ? `Active Deal: ${sellingToName}` : status, chipColor: 'info' }
    }
    return { label: status, chipColor: 'default' }
  }

  const getCurrentLocationLabel = (truckId: string, locationId?: string | null): string => {
    if (!locationId) return '—'
    const repair = repairShopLocations?.find((r) => r.id === locationId)
    if (repair) return repair.name || repair.contact_name || 'Repair shop'
    const pickup = pickupLocations?.find((p) => p.id === locationId)
    if (pickup) {
      const cityState = [pickup.city, pickup.state].filter(Boolean).join(', ')
      return cityState || pickup.contact_name || 'Pickup location'
    }
    return 'Unknown'
  }

  const resolveLocationLabel = (
    locationId: string,
    locationType: 'pickup' | 'repair'
  ): string => {
    if (locationType === 'repair') {
      const repair = repairShopLocations?.find((r) => r.id === locationId)
      return repair?.name || repair?.contact_name || 'Unknown'
    }
    const pickup = pickupLocations?.find((p) => p.id === locationId)
    return pickup?.contact_name || 'Unknown'
  }

  const customerFilterOptions = useMemo(() => {
    const counts = new Map<string, number>()
    const clientLabelById = new Map<string, string>()
    let opticCount = 0

    clientUsers.forEach((client) => {
      const fullName = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
      clientLabelById.set(client.id, fullName || client.email || client.id)
    })

    transportTrucksWithVehicles.forEach(({ shipmentPlans }) => {
      shipmentPlans.forEach((plan) => {
        const clientId = plan.client_user_id
        if (!clientId || clientId === 'optic') {
          opticCount += 1
          return
        }
        counts.set(clientId, (counts.get(clientId) ?? 0) + 1)
      })
    })

    const sortedIds = [...counts.keys()].sort((a, b) => {
      const labelA = clientLabelById.get(a) ?? a
      const labelB = clientLabelById.get(b) ?? b
      return labelA.localeCompare(labelB)
    })

    return [
      { value: 'ALL', label: 'All' },
      { value: OPTIC_FILTER_VALUE, label: OPTIC_FILTER_LABEL, count: opticCount },
      ...sortedIds.map((id) => ({
        value: id,
        label: clientLabelById.get(id) ?? id,
        count: counts.get(id) ?? 0,
      })),
    ]
  }, [transportTrucksWithVehicles, clientUsers])

  const filteredTrucks = useMemo(() => {
    return transportTrucksWithVehicles.filter(({ truck, vehicle, shipmentPlans }) => {
      const matchesCustomer =
        customerFilter === 'ALL' ||
        (customerFilter === OPTIC_FILTER_VALUE &&
          shipmentPlans.some((plan) => !plan.client_user_id || plan.client_user_id === 'optic')) ||
        shipmentPlans.some((plan) => plan.client_user_id === customerFilter)

      if (!matchesCustomer) return false

      const truckTransporterIds = new Set(
        shipmentPlans.flatMap((plan) =>
          (plan.shipment_legs ?? [])
            .map((leg) => leg.company_id)
            .filter((companyId): companyId is string => typeof companyId === 'string')
        )
      )
      const matchesTransporter =
        transporterFilter === 'ALL' || truckTransporterIds.has(transporterFilter)
      if (!matchesTransporter) return false

      const currentLocationKey = truck.currentLocationId ?? NO_CURRENT_LOCATION_FILTER_VALUE
      const matchesCurrentLocation =
        currentLocationFilter === 'ALL' || currentLocationKey === currentLocationFilter
      if (!matchesCurrentLocation) return false

      if (flaggedOnly) {
        const repairTruck = truck.currentLocationId
          ? repairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`)
          : null
        if (!repairTruck?.flagged) return false
      }

      if (!normalizedSearch) return true

      const customerDisplay = getCustomerDisplay(shipmentPlans, vehicle)
      const customerSearchTerms = [
        customerDisplay.label,
        vehicle?.status ?? '',
        ...shipmentPlans.flatMap((plan) => {
          const clientId = plan.client_user_id
          if (!clientId || clientId === 'optic') {
            return [OPTIC_FILTER_LABEL, 'optic']
          }
          const client = clientUserById.get(clientId)
          if (!client) return [clientId]
          const fullName = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
          return [fullName, client.email ?? '', client.id]
        }),
      ]

      const haystack = [
        truck.vin,
        vehicle?.stock_number,
        vehicle?.make,
        vehicle?.model,
        vehicle?.year != null ? String(vehicle.year) : '',
        ...customerSearchTerms,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(normalizedSearch)
    })
  }, [
    clientUserById,
    customerFilter,
    transporterFilter,
    currentLocationFilter,
    flaggedOnly,
    normalizedSearch,
    repairTrucksMap,
    transportTrucksWithVehicles,
  ])

  const associatedCompanyIds = useMemo(() => {
    const companyIds = new Set<string>()
    transportTrucksWithVehicles.forEach(({ shipmentPlans }) => {
      shipmentPlans.forEach((plan) => {
        ;(plan.shipment_legs ?? []).forEach((leg) => {
          if (!leg.company_id || NON_COMPANY_IDS.has(leg.company_id)) return
          companyIds.add(leg.company_id)
        })
      })
    })
    return [...companyIds]
  }, [transportTrucksWithVehicles])
  const { data: transporterGroupData } = useTransporterGroups(associatedCompanyIds)

  const transporterFilterOptions = useMemo(() => {
    const counts = new Map<string, number>()
    transportTrucksWithVehicles.forEach(({ shipmentPlans }) => {
      const transporterIdsForTruck = new Set<string>()
      shipmentPlans.forEach((plan) => {
        ;(plan.shipment_legs ?? []).forEach((leg) => {
          if (!leg.company_id) return
          transporterIdsForTruck.add(leg.company_id)
        })
      })
      transporterIdsForTruck.forEach((companyId) => {
        counts.set(companyId, (counts.get(companyId) ?? 0) + 1)
      })
    })

    const sortedIds = [...counts.keys()].sort((a, b) => {
      const labelA = formatTransporterDisplayName(
        a,
        transporterGroupData?.groupOwners?.[a]?.group_name
      )
      const labelB = formatTransporterDisplayName(
        b,
        transporterGroupData?.groupOwners?.[b]?.group_name
      )
      return labelA.localeCompare(labelB)
    })

    return [
      { value: 'ALL', label: 'All' },
      ...sortedIds.map((id) => ({
        value: id,
        label: formatTransporterDisplayName(
          id,
          transporterGroupData?.groupOwners?.[id]?.group_name
        ),
        count: counts.get(id) ?? 0,
      })),
    ]
  }, [transportTrucksWithVehicles, transporterGroupData])

  const currentLocationFilterOptions = useMemo(() => {
    const counts = new Map<string, number>()
    transportTrucksWithVehicles.forEach(({ truck }) => {
      const locationKey = truck.currentLocationId ?? NO_CURRENT_LOCATION_FILTER_VALUE
      counts.set(locationKey, (counts.get(locationKey) ?? 0) + 1)
    })

    const sortedKeys = [...counts.keys()].sort((a, b) => {
      const labelA =
        a === NO_CURRENT_LOCATION_FILTER_VALUE ? '—' : getCurrentLocationLabel('', a)
      const labelB =
        b === NO_CURRENT_LOCATION_FILTER_VALUE ? '—' : getCurrentLocationLabel('', b)
      return labelA.localeCompare(labelB)
    })

    return [
      { value: 'ALL', label: 'All' },
      ...sortedKeys.map((key) => ({
        value: key,
        label:
          key === NO_CURRENT_LOCATION_FILTER_VALUE
            ? '—'
            : getCurrentLocationLabel('', key),
        count: counts.get(key) ?? 0,
      })),
    ]
  }, [transportTrucksWithVehicles, pickupLocations, repairShopLocations])

  const rows = useMemo<VehiclesV2Row[]>(
    () =>
      filteredTrucks.map(({ truck, vehicle, shipmentPlans }) => {
        const yearMakeModel = vehicle
          ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim()
          : 'Unknown vehicle'
        const customerDisplay = getCustomerDisplay(shipmentPlans, vehicle)
        const inventoryStatusDisplay = getInventoryStatusDisplay(vehicle)
        const currentRepairTruck = truck.currentLocationId
          ? repairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`)
          : null
        const flagged = Boolean(currentRepairTruck?.flagged)
        const latestPlan = [...(shipmentPlans ?? [])].sort(
          (a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0)
        )[0]
        const truckStatus = getTruckStatus(truck)
        const nextActiveLeg = latestPlan?.shipment_legs?.find((leg) => getLegStatus(leg) !== 'Completed')
        const readyByNextLeg =
          !!nextActiveLeg &&
          nextActiveLeg.ready === true &&
          !!nextActiveLeg.company_id &&
          ['Draft', 'Pending driver', 'Pending completion', 'Assigned'].includes(
            getLegStatus(nextActiveLeg)
          )
        // If the truck status is already awaiting transport, always show pickup-ready.
        const readyForPickup = truckStatus === 'awaiting_transport' || readyByNextLeg
        const transporters = Array.from(
          new Set(
            shipmentPlans.flatMap((plan) =>
              (plan.shipment_legs ?? [])
                .map((leg) => leg.company_id)
                .filter((companyId): companyId is string => typeof companyId === 'string')
                .map((companyId) =>
                  formatTransporterDisplayName(
                    companyId,
                    transporterGroupData?.groupOwners?.[companyId]?.group_name
                  )
                )
            )
          )
        )
        const hasAnyLegPhotos = (shipmentPlans ?? []).some((plan) => {
          if (plan.vehicle_damage) return true
          return (plan.shipment_legs ?? []).some((leg) =>
            (leg.status_history ?? []).some((entry) =>
              (entry.notes ?? '').toLowerCase().includes('photo')
            )
          )
        })

        return {
          id: truck.id,
          inventoryVehicleId: vehicle?.id ?? null,
          vehicle: yearMakeModel || 'Unknown vehicle',
          stock: vehicle?.stock_number ?? '—',
          tier: vehicle?.tier || '—',
          vin: truck.vin ?? vehicle?.vin ?? '—',
          estimatedRepairCost: vehicle?.estimated_repair_cost ?? null,
          fundedDate: vehicle?.funded_date ?? null,
          paidDate: vehicle?.paid_for_date ?? vehicle?.title_tracking?.paid_date ?? null,
          customer: customerDisplay.label,
          inventoryStatus: inventoryStatusDisplay.label,
          inventoryStatusChipColor: inventoryStatusDisplay.chipColor,
          location: getCurrentLocationLabel(truck.id, truck.currentLocationId),
          status: truckStatus,
          flagged,
          canToggleFlag: Boolean(currentRepairTruck),
          hasAnyLegPhotos,
          readyForPickup,
          transporters,
          repairTruckId: currentRepairTruck?.id ?? null,
        }
      }),
    [filteredTrucks, repairTrucksMap, clientUserById, transporterGroupData]
  )

  const trucksById = useMemo(
    () => new Map(transportTrucksWithVehicles.map((truck) => [truck.truck.id, truck] as const)),
    [transportTrucksWithVehicles]
  )

  const timelineTruck = timelineTruckId ? trucksById.get(timelineTruckId) ?? null : null
  const repairTimelineTruck = repairTimelineTruckId
    ? trucksById.get(repairTimelineTruckId) ?? null
    : null
  const shipmentPlanTruck = shipmentPlanTruckId ? trucksById.get(shipmentPlanTruckId) ?? null : null

  const selectedRepairTimelineItem = useMemo<RepairTruckWithTruck | null>(() => {
    if (!repairTimelineTruck) return null
    const currentLocationId = repairTimelineTruck.truck.currentLocationId
    if (!currentLocationId) return null
    const repairTruck = repairTrucksMap.get(`${repairTimelineTruck.truck.id}_${currentLocationId}`)
    if (!repairTruck) return null
    return {
      repairTruck,
      truckWithVehicle: repairTimelineTruck,
    }
  }, [repairTimelineTruck, repairTrucksMap])

  const getUserName = (userId: string): string => {
    const user = users.find((u) => u.id === userId)
    if (!user) return 'Unknown'
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    return name || user.email || 'Unknown'
  }

  const getRepairShopLabelFromItem = (item: RepairTruckWithTruck): string => {
    const shop = repairShopLocations?.find((r) => r.id === item.repairTruck.repair_shop_location_id)
    return shop?.name || shop?.contact_name || 'Unknown shop'
  }

  const handleOpenShipmentPlanDialog = (truckId: string) => {
    const truckWithVehicle = trucksById.get(truckId)
    const latestPlan =
      truckWithVehicle?.shipmentPlans?.length
        ? [...truckWithVehicle.shipmentPlans].sort(
            (a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0)
          )[0]
        : null
    setShipmentPlanToEdit(latestPlan ?? null)
    setInsertRepairAfterLegIndex(null)
    setShipmentPlanTruckId(truckId)
  }

  const handleOpenLegPhotosDialog = (truckId: string) => {
    const truckWithVehicle = trucksById.get(truckId)
    if (!truckWithVehicle) return
    const latestPlan =
      truckWithVehicle.shipmentPlans?.length
        ? [...truckWithVehicle.shipmentPlans].sort(
            (a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0)
          )[0]
        : null
    if (!latestPlan?.id) return

    setLegPhotosState({
      planId: latestPlan.id,
      plan: latestPlan,
      legItems: (latestPlan.shipment_legs ?? []).map((leg) => {
        const history = leg.status_history
        const latestStatusNotes = history?.length
          ? history[history.length - 1]?.notes
          : undefined
        return {
          fromLabel: resolveLocationLabel(leg.from_id, leg.from_type),
          toLabel: resolveLocationLabel(leg.to_id, leg.to_type),
          company_id: leg.company_id,
          to_type: leg.to_type,
          latestStatusNotes,
        }
      }),
      vehicle_damage: latestPlan.vehicle_damage ?? undefined,
    })
  }

  const handleViewTransportCard = (truckId: string) => {
    setTransportCardTruckId(truckId)
  }

  const handleOpenInventoryForm = (inventoryVehicleId: string) => {
    setInventoryFormVehicleId(inventoryVehicleId)
  }

  return (
    <>
      <Box sx={{ px: 2, mt: 2 }}>
        <SearchHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search vehicles..."
          showSearch={true}
          showSoldToUserFilter={true}
          soldToUserFilter={customerFilter}
          onSoldToUserFilterChange={setCustomerFilter}
          soldToUserOptions={customerFilterOptions}
          showTransporterFilter={true}
          transporterFilter={transporterFilter}
          onTransporterFilterChange={setTransporterFilter}
          transporterOptions={transporterFilterOptions}
          showCurrentLocationFilter={true}
          currentLocationFilter={currentLocationFilter}
          onCurrentLocationFilterChange={setCurrentLocationFilter}
          currentLocationOptions={currentLocationFilterOptions}
          showFlaggedCheckbox={true}
          flaggedOnly={flaggedOnly}
          onFlaggedOnlyChange={setFlaggedOnly}
          showBulkUpdateButton={customerFilter !== 'ALL' && rows.length > 0}
          onBulkUpdateClick={() => setBulkNoteDialogOpen(true)}
          showDashboard={false}
        />
      </Box>

      <VehiclesV2Grid
        rows={rows}
        isLoading={isLoadingTransportTrucks}
        onOpenTimeline={(truckId) => setTimelineTruckId(truckId)}
        onOpenRepairTimeline={(truckId) => setRepairTimelineTruckId(truckId)}
        onOpenShipmentPlan={handleOpenShipmentPlanDialog}
        onOpenLegPhotos={handleOpenLegPhotosDialog}
        onOpenTransportTruck={handleViewTransportCard}
        onOpenInventoryForm={handleOpenInventoryForm}
        onToggleFlag={async (truckId, nextFlagged) => {
          const truckWithVehicle = trucksById.get(truckId)
          const currentLocationId = truckWithVehicle?.truck.currentLocationId
          if (!truckWithVehicle || !currentLocationId) return
          const repairTruck = repairTrucksMap.get(`${truckId}_${currentLocationId}`)
          if (!repairTruck) return
          await updateRepairTruckFlagged({ repairTruckId: repairTruck.id, flagged: nextFlagged })
        }}
        isTogglingFlag={isUpdatingRepairTruckFlagged}
      />

      <BulkCustomerVehiclesTimelineNoteDialog
        open={bulkNoteDialogOpen}
        onClose={() => setBulkNoteDialogOpen(false)}
        rows={rows}
      />

      {timelineTruck?.vehicle && (
        <TimelineDialog
          open={!!timelineTruck}
          onClose={() => setTimelineTruckId(null)}
          vehicle={timelineTruck.vehicle}
        />
      )}
      <RepairTimelineDialog
        open={!!selectedRepairTimelineItem}
        onClose={() => setRepairTimelineTruckId(null)}
        item={selectedRepairTimelineItem}
        getRepairShopLabel={getRepairShopLabelFromItem}
        getUserName={getUserName}
        onAddNote={async (id, payload) => {
          await addRepairTruckNote({
            repairTruckId: id,
            note: payload.note,
            visible_to_repair_users: payload.visible_to_repair_users,
          })
        }}
        onEditNote={async (id, noteIndex, note) => {
          await updateRepairTruckNote({ repairTruckId: id, noteIndex, note })
        }}
        onSetNoteVisibleToRepairUsers={async (id, noteIndex, visible) => {
          await updateRepairTruckNote({
            repairTruckId: id,
            noteIndex,
            visible_to_repair_users: visible,
          })
        }}
        onDeleteNote={async (id, noteIndex) => {
          await deleteRepairTruckNote({ repairTruckId: id, noteIndex })
        }}
        onUpdateWorkStatus={async (id, work_status) => {
          await updateRepairTruckWorkStatus({ repairTruckId: id, work_status })
        }}
        onReviewEstimate={async (id, noteIndex, status) => {
          await reviewRepairTruckEstimate({ repairTruckId: id, noteIndex, status })
        }}
        onUpdatePaid={async (id, paid) => {
          await updateRepairTruckPaid({ repairTruckId: id, paid })
        }}
        onUpdateBeingPainted={async (id, being_painted) => {
          await updateRepairTruckBeingPainted({ repairTruckId: id, being_painted })
        }}
        onToggleFlag={async (id, flagged) => {
          await updateRepairTruckFlagged({ repairTruckId: id, flagged })
        }}
        isAddingNote={isAddingRepairTruckNote}
        isEditingNote={isUpdatingRepairTruckNote}
        isDeletingNote={isDeletingRepairTruckNote}
        isUpdatingWorkStatus={isUpdatingRepairTruckWorkStatus}
        isReviewingEstimate={isReviewingRepairTruckEstimate}
        isUpdatingPaid={isUpdatingRepairTruckPaid}
        isUpdatingBeingPainted={isUpdatingRepairTruckBeingPainted}
        isUpdatingFlag={isUpdatingRepairTruckFlagged}
      />
      <ShipmentPlanDialog
        open={!!shipmentPlanTruck}
        onClose={() => {
          setShipmentPlanTruckId(null)
          setShipmentPlanToEdit(null)
          setInsertRepairAfterLegIndex(null)
        }}
        truckWithVehicle={shipmentPlanTruck}
        planToEdit={shipmentPlanToEdit}
        insertRepairAfterLegIndex={insertRepairAfterLegIndex}
      />
      <LegPhotosDialog
        open={!!legPhotosState}
        onClose={() => setLegPhotosState(null)}
        planId={legPhotosState?.planId ?? ''}
        legItems={legPhotosState?.legItems ?? []}
        initialLegIndex={legPhotosState?.initialLegIndex}
        vehicle_damage={legPhotosState?.vehicle_damage ?? undefined}
        onApprovePreDelivery={async (planId, legIndex) => {
          if (!legPhotosState?.plan || legPhotosState.plan.id !== planId) return
          await approveLegPhotos({ plan: legPhotosState.plan, legIndex })
        }}
        onRejectPreDelivery={(planId, legIndex) => {
          if (!legPhotosState?.plan || legPhotosState.plan.id !== planId) return
          const truckWithVehicle = shipmentPlanTruckId
            ? trucksById.get(shipmentPlanTruckId)
            : [...trucksById.values()].find((entry) =>
                entry.shipmentPlans.some((plan) => plan.id === planId)
              )
          if (!truckWithVehicle) return
          setShipmentPlanToEdit(legPhotosState.plan)
          setInsertRepairAfterLegIndex(legIndex)
          setShipmentPlanTruckId(truckWithVehicle.truck.id)
        }}
      />
      <EditVehicleForm
        open={!!inventoryFormVehicleId}
        vehicleId={inventoryFormVehicleId ?? undefined}
        onClose={() => setInventoryFormVehicleId(null)}
        inventoryCollection="master_inventory"
      />
      <TransportTruckCardDialog
        open={!!transportCardTruckId}
        onClose={() => setTransportCardTruckId(null)}
        truckId={transportCardTruckId}
      />
    </>
  )
}

export default VehiclesTabV2
